import { HeaderFooter } from './HeaderFooter';
import { useState, useEffect } from 'react';
import {
  FGInputField,
  FGPassword,
  FGIcons,
  IconsLarge,
  FGTypography,
  FGButton,
  FGInlineAlert,
} from '@fg/design-system';
import clsx from 'clsx';
import { resetPasswordDispatch } from '../services/Login';

type Props = {
  customProps: {
    logoType: string;
    appCode: string;
  }
};
export const CreatePassword = ({ customProps }: Props) => {
  const id = 'create-password-view';
  const allowedUrls = [
    'http://localhost:8080',
    'https://app.fitchconnect-dev.com',
    'https://app.fitchconnect-qa.com',
    'https://app.fitchconnect-stg.com',
    'https://app.fitchconnect.com',
  ];
  const [newPassword, setNewPassword] = useState();
  const [confirmPassword, setConfirmPassword] = useState();
  const [passwordMatched, setPasswordMatched] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const queryParams = new URLSearchParams(window.location.search);
  const { appCode, logoType } = customProps;
  const showDisclaimerFooter = appCode === 'frp';
  const nonceToken = queryParams.get('nonce');
  const attemptedUrl = queryParams.get('attemptedUrl');
  const newPasswordConstant = 'new-password';
  const PasswordTooltip: React.FC<{ value: string }> = ({ value }) => {
    const has8Characters = value.length >= 8;
    const has1Number = !!value.match(/[0-9]/g)?.length;
    const upperCaseLetter = /[A-Z]/.test(value);
    const lowerCaseLetter = /[a-z]/.test(value);

    const Item: React.FC<{ children: any; isValid: boolean }> = ({ children, isValid }) => (
      <li
        className="fg-d--f fg-ai--c fg-mg--b1"
        style={{
          color: isValid ? 'var(--fg-green-green-700)' : 'var(--fg-grey-grey-600)',
        }}
      >
        <FGIcons large={IconsLarge.Check2} className="fg-mg--r2" />
        {children}
      </li>
    );

    return (
      <div>
        <h3 className="fg-body4 fg-mg--b1" style={{ color: 'var(--fg-primary-black)' }}>
          New password must contain
        </h3>
        <ol className="fg-body4" style={{ listStyle: 'none' }}>
          <Item isValid={has8Characters}>At least 8 characters</Item>
          <Item isValid={has1Number}>At least 1 number</Item>
          <Item isValid={upperCaseLetter}>At least one upper case character</Item>
          <Item isValid={lowerCaseLetter}>At least one lower case character</Item>
        </ol>
      </div>
    );
  };
  const [newPasswordTooltipContent, setNewPasswordTooltipContent] = useState(<PasswordTooltip value="" />);
  const [confirmPasswordTooltipContent, setConfirmPasswordTooltipContent] = useState(<PasswordTooltip value="" />);

  const isValidAttemptedUrl = (attemptedUrl) => {
    return allowedUrls.some((url) => attemptedUrl.startsWith(url));
  };

  const isPasswordValid = (value) => {
    const has8Characters = value.length >= 8;
    const has1Number = !!value.match(/[0-9]/g)?.length;
    const upperCaseLetter = /[A-Z]/.test(value);
    const lowerCaseLetter = /[a-z]/.test(value);
    return has8Characters && has1Number && upperCaseLetter && lowerCaseLetter;
  };

  const handleChange = (e, input) => {
    if (input === newPasswordConstant) {
      setNewPassword(e.target.value);
    } else {
      setConfirmPassword(e.target.value);
    }
  };

  const tooltipContentLogic = (e: React.ChangeEvent<HTMLInputElement>, input: 'new-password' | 'confirm-password') => {
    const component = <PasswordTooltip value={e.target.value} />;
    input === newPasswordConstant ? setNewPasswordTooltipContent(component) : setConfirmPasswordTooltipContent(component);
    handleChange(e, input);
  };

  useEffect(() => {
    if (newPassword && confirmPassword && newPassword === confirmPassword && isPasswordValid(newPassword)) {
      setPasswordMatched(true);
    } else {
      setPasswordMatched(false);
    }
  }, [newPassword, confirmPassword]);

  const resetPasswordAPICall = () => {
    const payload = {
      password: newPassword,
      token: nonceToken,
      appCode: appCode
    };
    setErrorMessage('');
    return resetPasswordDispatch(payload)
      .then(() => {
        window.location.href = attemptedUrl && isValidAttemptedUrl(attemptedUrl) ? attemptedUrl : '/login';
      })
      .catch((error) => {
        console.log('error', error);
        setErrorMessage('There was an error resetting the password. Please try again.');
      });
  };

  const handleKeypress = (e) => {
    if (e.charCode === 13 && passwordMatched) {
      resetPasswordAPICall();
    }
  };
  return (
    <>
      <HeaderFooter appCode={appCode} logoType={logoType} showDisclaimerFooter={showDisclaimerFooter}>
        <div className="fg-create-password-story">
          {errorMessage && (
            <div className={clsx('fg-ta--c', 'fg-error-banner-story__banner-wrapper')}>
              <FGInlineAlert type="error">{errorMessage}</FGInlineAlert>
            </div>
          )}
          <div
            className="fg-pd--x5 fg-fxd--c fg-d--f fg-create-password-story__content-wrapper"
            style={{
              maxWidth: '424px',
            }}
          >
            <FGTypography tag="h4" style="headline4" className="fg-ta--c fg-mg--b2">
              <span style={{ color: 'var(--fg-grey-grey-1000)' }}>Please create a password</span>
            </FGTypography>
            <p className="fg-mg--b10 fg-body3-long fg-ta--c" style={{ color: 'var(--fg-grey-grey-700)' }}>
              Must contain at least 8 characters and a mixture of <br />
              numbers and letters
            </p>
            <FGInputField id={`${id}-new-password`} label="New Password">
              <FGPassword
                className="xxx"
                id={`${id}-new-password`}
                tooltipContent={newPasswordTooltipContent}
                onChange={(e: any) => tooltipContentLogic(e, newPasswordConstant)}
                autoComplete="off"
                onKeyPress={handleKeypress}
                size="l"
              />
            </FGInputField>
            <FGInputField id={`${id}-confirm-password`} label="Confirm Password">
              <FGPassword
                id={`${id}-confirm-password`}
                tooltipContent={confirmPasswordTooltipContent}
                onChange={(e: any) => tooltipContentLogic(e, 'confirm-password')}
                size="l"
                rightElement={<FGIcons large={IconsLarge.Check2} style={{ color: 'var(--fg-green-green-700' }} />}
                autoComplete="off"
                onKeyPress={handleKeypress}
              />
            </FGInputField>
            <div className="fg-pd--t10" style={{ display: 'inline-grid' }}>
              <FGButton size="xl" disabled={!passwordMatched} className="pi-reset-password-button"
                onClick={resetPasswordAPICall}>
                Continue
              </FGButton>
            </div>
          </div>
        </div>
      </HeaderFooter>
    </>
  );
};
